import React from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { useTranslation } from 'react-i18next';
import { solutionsArticleUrl } from '../config/solutionsArticles';

/**
 * Help icon opening a help center article in a new tab: an outlined question mark in the brand
 * tone (white on the purple page heading), no disc behind it (index.css .pulse-button - the name
 * outlived the pulse ring).
 *
 *   <SolutionsArticleLink article="glossary" placement="heading" />
 *
 * `article` is a key from config/solutionsArticles.js - add the article there, never a url
 * here. `placement` covers the spots the app puts these in, so no call site has to hand-tune
 * offsets again; `rootStyle` still overrides everything for a one-off.
 */
const PLACEMENTS = {
  // page heading (the h4), floated right of the title
  heading: { float: 'right', position: 'relative', top: 8, right: 12, fontSize: 20 },
  // same, for a heading that also carries an OptionsMenu - the offsets put this icon's
  // centre on the three-dot icon's, which sits 7px higher than the text baseline suggests
  headingMenu: { float: 'right', position: 'relative', top: 13, right: 4, fontSize: 20 },
  // a single setting: top right corner of the surrounding fieldset or card. zIndex because a
  // relatively positioned sibling that comes later - a FormLabel - otherwise paints over it
  setting: { position: 'absolute', top: 12, right: 12, zIndex: 1 },
  // straight after a title that is not a page heading, e.g. a collapsable section's
  inline: { position: 'relative', display: 'inline-block', top: 3, marginLeft: 10, fontSize: 20 },
  // inline geometry on a page heading that sits in a flex box (no float), white like the heading
  headingInline: { position: 'relative', display: 'inline-block', top: 3, marginLeft: 10, fontSize: 20 },
  // in an expanded list row's header, left of its prev/next arrows
  rowHeader: { position: 'relative', display: 'inline-block', top: 10, marginRight: 8, fontSize: 20 },
  // a dialog title - right of the title, clear of the close button in the corner
  dialogTitle: { float: 'right', position: 'relative', top: 8, right: 40, fontSize: 20 },
};

const SolutionsArticleLink = ({ article, url, subject, placement, rootStyle, iconStyle }) => {
  const { t } = useTranslation();
  const href = url || solutionsArticleUrl(article);
  // naming the section beats "on this" - pass the already translated label
  const tooltip = subject
    ? t('system.solutionsArticleTooltipOn', { subject })
    : t('system.solutionsArticleTooltip');

  // stopPropagation: the icon also sits in headers that toggle a section on click
  const open = (event) => {
    if(event) {
      event.stopPropagation();
    }
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  return (
    <span style={rootStyle || PLACEMENTS[placement] || PLACEMENTS.setting}>
      <div style={{position: 'relative', display: 'inline-block'}}>
        <Tooltip slotProps={{ tooltip: { sx: { maxWidth: 500 } } }}
          title={
            <Typography variant="caption">
              <OpenInNewRoundedIcon style={{fontSize: 14, marginTop: -1}} />{' '}
              {tooltip}
            </Typography>
          }
          aria-label={tooltip}
          arrow
        >
          <span className={['heading', 'headingMenu', 'headingInline'].includes(placement) ? 'pulse-button on-banner' : 'pulse-button'}
            role="button"
            tabIndex={0}
            aria-label={tooltip}
            onClick={open}
            onKeyDown={event => {
              if(event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                open(event);
              }
            }}
          >
            <HelpOutlineRoundedIcon style={iconStyle ? iconStyle : {
              fontSize: 'inherit'
            }}/>
          </span>
        </Tooltip>
      </div>
    </span>
  );
};

SolutionsArticleLink.propTypes = {
  // key from config/solutionsArticles.js - the normal way in
  article: PropTypes.string,
  // full url, for an article that has no registry entry yet
  url: PropTypes.string,
  // translated name of what the article is about, e.g. the page title
  subject: PropTypes.string,
  placement: PropTypes.oneOf(Object.keys(PLACEMENTS)),
  rootStyle: PropTypes.object,
  iconStyle: PropTypes.object,
};

export default SolutionsArticleLink;
